import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { Timestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '../components/firebase-config';
import { AppConfig, APP_DEFAULTS } from '../config';

interface AppConfigContextProps {
  config: AppConfig;
  loading: boolean;
}

const defaultConfig: AppConfig = {
  brandName: APP_DEFAULTS.brandName,
  loaderDelay: APP_DEFAULTS.loaderDelay,
  spinPromptLabel: APP_DEFAULTS.spinPromptLabel,
  primaryColor: APP_DEFAULTS.primaryColor,
  accentColor: APP_DEFAULTS.accentColor,
  winMessage: APP_DEFAULTS.winMessage,
  failMessage: APP_DEFAULTS.failMessage,
  accountRequiredMessage: APP_DEFAULTS.accountRequiredMessage,
  taglines: APP_DEFAULTS.taglines,
  carouselInterval: APP_DEFAULTS.carouselInterval,
  maxSpinsPerUser: APP_DEFAULTS.maxSpinsPerUser,
  formFields: APP_DEFAULTS.formFields,
  countdownTargetDate: APP_DEFAULTS.countdownTargetDate,
  campaignEnabled: APP_DEFAULTS.campaignEnabled,
  campaignEndsAt: APP_DEFAULTS.campaignEndsAt,
  allowLocalTimerOverride: APP_DEFAULTS.allowLocalTimerOverride,
  logoUrl: APP_DEFAULTS.logoUrl,
  homeWheelCenterImageUrl: APP_DEFAULTS.homeWheelCenterImageUrl,
};

const AppConfigContext = createContext<AppConfigContextProps>({
  config: defaultConfig,
  loading: true,
});

const toMillisOrFallback = (
  value: unknown,
  fallback: number | null
): number | null => {
  if (value instanceof Timestamp) {
    return value.toMillis();
  }

  if (typeof value === 'number') {
    return value;
  }

  if (value === null) {
    return null;
  }

  return fallback;
};

const normalizeRemoteConfig = (remote: Partial<AppConfig> & Record<string, unknown>): Partial<AppConfig> => ({
  ...remote,
  countdownTargetDate:
    toMillisOrFallback(remote.countdownTargetDate, APP_DEFAULTS.countdownTargetDate) ?? 0,
  campaignEndsAt: toMillisOrFallback(remote.campaignEndsAt, APP_DEFAULTS.campaignEndsAt),
});

export const AppConfigProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<AppConfig>(defaultConfig);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const docRef = doc(
          db,
          APP_DEFAULTS.firestoreConfigCollection,
          APP_DEFAULTS.firestoreConfigId
        );
        const docSnap = await getDoc(docRef);
        console.log('[AppConfig] Fetching Firestore config from config/app', {
          exists: docSnap.exists(),
          projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
        });
        if (docSnap.exists()) {
          const remote = docSnap.data() as Partial<AppConfig> & Record<string, unknown>;
          const normalized = normalizeRemoteConfig(remote);
          console.log('[AppConfig] Raw Firestore config:', remote);
          console.log('[AppConfig] Normalized config:', normalized);
          setConfig((prev) => ({ ...prev, ...normalized }));
        } else {
          console.warn('[AppConfig] Firestore document config/app does not exist. Using defaults.');
        }
      } catch (error) {
        console.error('Failed to load app config from Firestore, using defaults:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  useEffect(() => {
    document.documentElement.style.setProperty('--color-primary', config.primaryColor);
    document.documentElement.style.setProperty('--color-accent', config.accentColor);
  }, [config.primaryColor, config.accentColor]);

  const value = useMemo(
    () => ({
      config,
      loading,
    }),
    [config, loading]
  );

  return <AppConfigContext.Provider value={value}>{children}</AppConfigContext.Provider>;
};

export const useAppConfig = () => useContext(AppConfigContext);

export default AppConfigContext;
